import { KeyboardArrowDown } from "@mui/icons-material"
import RefreshIcon from "@mui/icons-material/Refresh"
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard"
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { Locker } from "shared/locker"
import { LockerDoorReservationSession } from "shared/lockerDoorReservationSession"
import { LockerDoorReservationSessionThingTypeMrn } from "shared/lockerDoorReservationSessionData"
import { Reservation } from "shared/reservation"
import { ReservationThingTypeMrn } from "shared/reservationData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import type { Sst2Door, Sst2Locker, Sst2TemplateAssociation } from "shared/sst2"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { selectMeIsReady, selectTenantId } from "src/features/me/meSlice"
import {
  useCreateThingsMutation,
  useDeleteThingsMutation,
  useGetThingsQuery,
} from "src/features/things/thingsApi"
import {
  selectCreatingReservationDoorId,
  selectHighlightedReservationId,
  selectSelectedDoorReservation,
  selectThingsByThingTypeMrnFilter,
  thingsActions,
} from "src/features/things/thingsSlice"
import useLockerDisplayValues from "src/hooks/useLockerDisplayValues"
import { v4 as uuid } from "uuid"
import mobilusLogo from "../assets/mobilus_logo.png"
import tabletIcon from "../assets/tablet.svg"
import { isSst2ReservationVisible } from "../sst2Status"
import {
  buildDepositReportModules,
  buildV2DepositGrids,
  printDepositReport,
} from "./depositReport"
import { orderedV2Modules } from "./lockerModuleOrder"
import { OsLockerDoorV2 } from "./OsLockerDoorV2"
import { Sl2Btn } from "./Sl2Btn"

type OsLockerDashboardV2Props = {
  locker: Locker
}

// Match V1's lockerDimRatio for consistent door sizing
const lockerDimRatio = 0.6

const TemplateGridLayout: FC<{
  template: Sst2TemplateAssociation
  doors: Sst2Door[]
  selectedDoorId: string | null
  onDoorClick: (door: Sst2Door) => void
  moduleLabel: string
  unlinkedDoorIds: Set<string>
  creatingReservationDoorId?: string
  sessionByDoorId: Record<string, LockerDoorReservationSession>
  onRemoveReservationSession: (sessionId: string) => void
}> = React.memo(
  ({
    template,
    doors,
    selectedDoorId,
    onDoorClick,
    moduleLabel,
    unlinkedDoorIds,
    creatingReservationDoorId,
    sessionByDoorId,
    onRemoveReservationSession,
  }) => {
    const cells = template.template?.cells || []
    const doorCells = cells.filter((c) => c.cell_type.toUpperCase() === "DOOR")
    const posCells = cells.filter((c) => c.cell_type.toUpperCase() === "POS" || c.cell_type === "screen")
    const width = template.template?.width ?? 400
    const height = template.template?.height ?? 600

    return (
      <Box
        sx={{
          width: `${lockerDimRatio * width + 40}px`,
          height: `${lockerDimRatio * height + 40}px`,
          paddingRight: "5px",
          paddingLeft: "5px",
          position: "relative",
          display: "inline-block",
          flexShrink: 0,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 0,
            pt: 3,
            bgcolor: "grey.100",
            position: "relative",
            height: "100%",
            borderRadius: 10,
            borderWidth: "5px",
          }}
        >
          {/* Module header inside Paper */}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" p={2}>
            <Typography variant="body1" fontWeight="bold">
              {moduleLabel}
            </Typography>
          </Stack>

          {/* Door grid container — absolute positioning like V1 */}
          <Box
            sx={{
              display: "flex",
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            {doorCells.map((cell) => {
              // doors are pre-scoped to this template via locker_group, so
              // matching by cell_id is unambiguous within that subset.
              const door = doors.find((d) => d.cell_id === cell.id)
              if (!door) return null
              return (
                <Box
                  key={door.id}
                  sx={{
                    position: "absolute",
                    top: `${cell.y * lockerDimRatio}px`,
                    left: `${cell.x * lockerDimRatio}px`,
                    width: `${cell.width * lockerDimRatio}px`,
                    height: `${cell.height * lockerDimRatio}px`,
                  }}
                >
                  <OsLockerDoorV2
                    door={door}
                    isSelected={selectedDoorId === door.id}
                    onClick={unlinkedDoorIds.has(door.id) ? undefined : () => onDoorClick(door)}
                    cellSize={cell.size}
                    isUnlinked={unlinkedDoorIds.has(door.id)}
                    isCreatingReservation={creatingReservationDoorId === door.id}
                    session={sessionByDoorId[door.id]}
                    onRemoveReservationSession={onRemoveReservationSession}
                  />
                </Box>
              )
            })}
            {posCells.map((cell) => (
              <Box
                key={`pos-${cell.id}`}
                sx={{
                  position: "absolute",
                  top: `${cell.y * lockerDimRatio}px`,
                  left: `${cell.x * lockerDimRatio}px`,
                  width: `${cell.width * lockerDimRatio}px`,
                  height: `${cell.height * lockerDimRatio}px`,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={tabletIcon}
                  alt="payment terminal"
                  width={`${cell.width * lockerDimRatio * 0.8}px`}
                  height="auto"
                  style={{
                    transform: cell.width > cell.height ? "none" : "rotate(90deg)",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    )
  },
)
TemplateGridLayout.displayName = "TemplateGridLayout"

export const OsLockerDashboardV2: FC<OsLockerDashboardV2Props> = React.memo(
  ({ locker }) => {
    const { t, i18n } = useTranslation()
    const l = useLockerDisplayValues(locker)
    const dispatch = useAppDispatch()
    const [createThings] = useCreateThingsMutation()
    const [deleteThings] = useDeleteThingsMutation()
    const tenantId = useAppSelector(selectTenantId)
    const isReady = useAppSelector(selectMeIsReady)

    const selectedDoorReservation = useAppSelector(selectSelectedDoorReservation)
    const creatingReservationDoorId = useAppSelector(
      selectCreatingReservationDoorId,
    )
    const highlightedReservationId = useAppSelector(
      selectHighlightedReservationId,
    )

    const sst2Locker = locker.sst2Locker as Sst2Locker | undefined

    const doors = useMemo(
      () => [...(sst2Locker?.doors || [])].sort((a, b) => a.door_number - b.door_number),
      [sst2Locker?.doors],
    )

    // Display order: `visual_order` as the base, then the locker's saved
    // `moduleMapping` on top (LOCKER-390 — V1 has always applied it, V2 did
    // not). The original templates_association index rides along through the
    // sort because doors reference their template via `door.locker_group`,
    // which indexes the array as the API returned it.
    const templates = useMemo(
      () => orderedV2Modules(sst2Locker, locker.moduleMapping),
      [sst2Locker, locker.moduleMapping],
    )

    const hasTemplates = templates.length > 0

    // Fetch SL2 Reservations for this locker (mirrors V1 OsLockerDashboard behavior)
    useGetThingsQuery(
      {
        params: {
          "ap.name": SharedAccessPatternOsEnum.GetLockerCurrentReservations,
          "ap.tenantId": tenantId,
          "ap.lockerId": locker.id,
          "ap.limit": 2000,
          tenantId,
        },
      },
      {
        skip: !tenantId || !isReady || !locker.id,
      },
    )

    const reservationFilter = useCallback(
      (thing: Reservation) => thing.lockerId === locker.id,
      [locker.id],
    )

    const reservations = useAppSelector(
      (s) =>
        selectThingsByThingTypeMrnFilter(
          s,
          ReservationThingTypeMrn,
          reservationFilter,
        ),
      (a, b) => JSON.stringify(a) === JSON.stringify(b),
    ) as unknown as Reservation[]

    // DEV-201 — soft locks held on this locker's doors. They arrive from the
    // same GetLockerCurrentReservations access pattern queried above (its
    // session query is version-agnostic) and stay live cross-operator via the
    // generic IoT ThingCreated/ThingDeleted fan-out.
    const sessionFilter = useCallback(
      (thing: LockerDoorReservationSession) =>
        thing.lockerId === locker.id && thing.tenantId === tenantId,
      [locker.id, tenantId],
    )

    const sessions = useAppSelector((s) =>
      selectThingsByThingTypeMrnFilter(
        s,
        LockerDoorReservationSessionThingTypeMrn,
        sessionFilter,
      ),
    ) as unknown as LockerDoorReservationSession[]

    const sessionByDoorId = useMemo(
      () =>
        sessions.reduce<Record<string, LockerDoorReservationSession>>(
          (acc, session) => {
            if (session.sstDoorId) acc[session.sstDoorId] = session
            return acc
          },
          {},
        ),
      [sessions],
    )

    const onRemoveReservationSession = useCallback(
      async (sessionId: string) => {
        try {
          await deleteThings({
            body: {
              things: [
                {
                  thingTypeMrn: LockerDoorReservationSessionThingTypeMrn,
                  tenantId,
                  lockerId: locker.id,
                  id: sessionId,
                },
              ],
            },
            params: { tenantId },
          }).unwrap()
        } catch (e) {
          console.error("removeReservationSession error", e)
        }
      },
      [deleteThings, locker.id, tenantId],
    )

    const [isRemovingAllSessions, setIsRemovingAllSessions] = useState(false)
    const [isRemovingAllSessionsConfirming, setIsRemovingAllSessionsConfirming] =
      useState(false)

    const removeAllSessions = useCallback(async () => {
      setIsRemovingAllSessions(true)
      try {
        await deleteThings({
          body: {
            things: sessions.map((session) => ({
              thingTypeMrn: LockerDoorReservationSessionThingTypeMrn,
              tenantId,
              lockerId: locker.id,
              id: session.id,
            })),
          },
          params: { tenantId },
        }).unwrap()
      } catch (e) {
        console.error("removeAllSessions error", e)
      } finally {
        setIsRemovingAllSessionsConfirming(false)
        setIsRemovingAllSessions(false)
      }
    }, [deleteThings, locker.id, sessions, tenantId])

    // Action menu (deposit report) — V1 had this in OsLockerDashboard; it was
    // never ported to V2. Minimal port: one anchor + one menu item.
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const actionMenuOpen = Boolean(anchorEl)
    const actionMenuHandleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
      },
      [],
    )
    const actionMenuHandleClose = useCallback(() => {
      setAnchorEl(null)
    }, [])

    // Driver deposit sheet: the parcels still to be dropped off at this locker,
    // with door + recipient + deposit code, opened in a print-ready window.
    const onGenerateDepositReport = useCallback(() => {
      // Build the grids in the SAME order the V2 dashboard assigns its A/B/C
      // module letters — visual_order then the saved moduleMapping (see
      // buildV2DepositGrids) — so the printed module letters match the
      // on-screen signage.
      const modules = buildDepositReportModules(
        reservations,
        buildV2DepositGrids(sst2Locker, locker.moduleMapping),
        t("module"),
      )
      const address = [
        l?.address1,
        l?.city,
        l?.province,
        l?.postalCode,
        l?.country,
      ]
        .filter((p) => p && p !== "-")
        .join(", ")
      const logoSrc = mobilusLogo
        ? new URL(mobilusLogo, window.location.origin).href
        : ""
      printDepositReport(
        modules,
        l?.i18n?.name || "-",
        address,
        {
          title: t("depositReportTitle"),
          door: t("door"),
          name: t("name"),
          depositCode: t("depositCode"),
          total: t("total"),
          empty: t("depositReportEmpty"),
          generatedAt: t("depositReportGeneratedAt"),
          generatedAtValue: new Date().toLocaleString(i18n.language),
        },
        logoSrc,
      )
    }, [reservations, sst2Locker, locker.moduleMapping, l, t, i18n.language])

    const onDoorClick = useCallback(
      (door: Sst2Door) => {
        // Find the V2 door's active embedded reservation
        const activeV2Res = (door.reservations || []).find(isSst2ReservationVisible)

        // Match the V2 embedded reservation to the SL2 Reservation in Redux
        const reservationId = activeV2Res
          ? reservations.find((r) => r.sstReservation?._id === activeV2Res.id)?.id
          : undefined

        dispatch(
          thingsActions.setDoorReservationIds({
            doorId: door.id,
            lockerId: locker.id,
            reservationId: reservationId as any,
          }),
        )
      },
      [dispatch, locker.id, reservations],
    )

    // LOCKER-389 — client-search result -> V2 door drawer.
    //
    // The search bar's only output is `highlightedReservationId`. Its sole
    // consumer was the V1 door tile (`OsLockerDoor`), which self-detects the
    // match and fires its own `onClick`; V2 door tiles never read it, so the
    // dispatch landed and nothing opened. This is V2's counterpart: resolve
    // the reservation to a door here — the dashboard already holds both lists
    // — and reach the same action an explicit click uses.
    //
    // `reservations` is scoped to THIS locker (`reservationFilter`), and the
    // door must exist on this locker's `doors`. The search itself is
    // store-wide, so those two guards are what keep a hit belonging to another
    // locker from opening an unrelated door here — the same containment V1 got
    // for free from only rendered tiles running the effect.
    useEffect(() => {
      if (!highlightedReservationId) return

      const reservation = reservations.find(
        (r) => r.id === highlightedReservationId,
      )
      if (!reservation) return

      // Reverse of the click path: the SL2 Reservation's embedded SST
      // reservation carries the V2 door it occupies.
      const doorId = reservation.sstReservation?.door?.door_id
      if (!doorId || !doors.some((d) => d.id === doorId)) return

      dispatch(
        thingsActions.setDoorReservationIds({
          doorId,
          lockerId: locker.id,
          reservationId: reservation.id,
        }),
      )
    }, [highlightedReservationId, reservations, doors, dispatch, locker.id])

    const onRefresh = useCallback(async () => {
      const refreshAction = {
        thingTypeMrn: ActionReqThingTypeMrn,
        executionMode: ActionReqExecutionModeEnum.sync,
        actionType: ActionReqTypeEnum.SstLockerRefresh,
        idempotencyKey: uuid(),
        delaySeconds: 2,
        tenantId: locker.tenantId,
        inputData: {
          tenantId: locker.tenantId,
          lockerId: locker.id,
          sstLockerId: locker.sstLockerId || "",
        },
      }
      try {
        await createThings({
          body: { things: [refreshAction] },
          params: { tenantId: locker.tenantId },
        }).unwrap()
      } catch (error) {
        console.error("V2 refresh error", error)
      }
    }, [createThings, locker.id, locker.sstLockerId, locker.tenantId])

    // No V2 locker data
    if (!sst2Locker) {
      return (
        <Alert severity="warning" sx={{ m: 2 }}>
          {t("lockerNotPaired")}
        </Alert>
      )
    }

    // Detect doors with a SST <-> SL2 data desync. Two directions:
    //
    //   1. SST has an active reservation that SL2 doesn't know about
    //      (operator created it via SST directly, OR SL2 missed the
    //      SQS event). The door is busy; we render "Indisponible"
    //      so the operator doesn't accidentally re-book it.
    //
    //   2. SL2 has an active Reservation linked to this door but the
    //      SST snapshot reports no visible reservation. Most likely a
    //      stale `sst2Locker` cached between a backend `syncLocker`
    //      poll and the next IoT broadcast — but it could also be a
    //      real data-loss bug. Either way the dashboard MUST NOT show
    //      "Disponible" while SL2 holds an active reservation: an
    //      operator could deposit on a door that already holds a parcel.
    //
    // Both directions render the same "Indisponible" placeholder, which
    // signals "needs attention, data out of sync — refresh the locker".
    //
    // LOCKER-321 — single source of truth: this cross-check is only SAFE
    // because both of its inputs are now RECONCILED MONOTONICALLY at the
    // reducer level (thingsSlice + sst2Reconcile). A reservation the client
    // has observed reach a terminal SST status is tombstoned and can never
    // be resurrected into CACHE-L (`d.reservations`, Direction 1) by a
    // stale locker snapshot, nor revert CACHE-R (`r.sstReservation.status`,
    // Direction 2) to a visible status. That makes the RACE-INDUCED false
    // gray (a just-closed reservation ghosting back after pickup)
    // structurally impossible here. What remains is only GENUINE desync:
    // Direction 1 = a real out-of-band SST reservation, Direction 2 = a
    // real stale-SST / data-loss gap. Both are legitimate signals to keep.
    const unlinkedDoorIds = useMemo(() => {
      const ids = new Set<string>()
      for (const d of doors) {
        // Direction 1: SST has an active reservation, SL2 doesn't.
        const activeV2Res = (d.reservations || []).find(isSst2ReservationVisible)
        if (activeV2Res) {
          const hasMatch = reservations.some(
            (r) => r.sstReservation?._id === activeV2Res.id,
          )
          if (!hasMatch) {
            ids.add(d.id)
            continue
          }
        } else {
          // Direction 2: SST says door is empty, but SL2 has an active
          // Reservation linked to this exact door. Match the SL2
          // reservation's embedded `sstReservation.door.door_id` against
          // the V2 door's `id`. We treat the SL2 reservation as "active"
          // when its embedded sstReservation.status is non-terminal —
          // same predicate as the SST V2 side, so the two checks agree on
          // what "active" means.
          const sl2Active = reservations.some(
            (r) =>
              r.sstReservation?.door?.door_id === d.id &&
              isSst2ReservationVisible(r.sstReservation as any),
          )
          if (sl2Active) {
            ids.add(d.id)
          }
        }
      }
      return ids
    }, [doors, reservations])

    // Count occupancy
    const totalDoors = doors.length
    const occupiedDoors = doors.filter(
      (d) => (d.reservations || []).some(isSst2ReservationVisible),
    ).length

    return (
      <Box data-ui="UI-SL2W-047">
        {/* Header — matches V1 layout: spacing 2, p 1, pl 4 */}
        <Stack direction="column" spacing={2} p={1} pl={4}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SpaceDashboardIcon sx={{ color: "secondary.main" }} />
              <Typography variant="h6" sx={{ color: "primary.main" }}>
                {l.i18n.name}
              </Typography>
              <IconButton onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                variant="body2"
                color={sst2Locker.is_online ? "green" : "error"}
              >
                {t(sst2Locker.is_online ? "online" : "offline")}
              </Typography>
              <Typography variant="body2">
                {`${totalDoors} ${t("doors")}`}
              </Typography>
              <Sl2Btn
                variant="outlined"
                endIcon={<KeyboardArrowDown />}
                onClick={actionMenuHandleClick}
              >
                {t("actions")}
              </Sl2Btn>
              <Menu
                anchorEl={anchorEl}
                open={actionMenuOpen}
                onClose={actionMenuHandleClose}
              >
                <MenuItem
                  onClick={() => {
                    onGenerateDepositReport()
                    actionMenuHandleClose()
                  }}
                  disableRipple
                >
                  {t("generateDepositReport")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setIsRemovingAllSessionsConfirming(true)
                    actionMenuHandleClose()
                  }}
                  disableRipple
                  disabled={isRemovingAllSessions || sessions.length === 0}
                >
                  {t("removeAllReservationSessions")}
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>

          {/* Occupancy — matches V1's LinearProgress style */}
          {totalDoors > 0 && (
            <Stack direction="column" spacing={0}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                color="grey.500"
              >
                <Typography variant="body2">{t("occupancy")}</Typography>
                <Typography variant="body2">
                  {`${Math.round((occupiedDoors / totalDoors) * 100)} %`}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={(occupiedDoors / totalDoors) * 100}
                sx={{
                  width: "100%",
                  backgroundColor: "lightgrey",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor:
                      occupiedDoors / totalDoors > 0.75
                        ? "red"
                        : occupiedDoors / totalDoors > 0.5
                          ? "orange"
                          : "green",
                  },
                }}
              />
            </Stack>
          )}
        </Stack>

        {/* Door grid — matches V1's mx 2, mt 1 layout */}
        <Box mx={2} mt={1} sx={{ whiteSpace: "nowrap", overflowX: "auto" }}>
          {hasTemplates ? (
            // Render one module per template, in the order `templates` already
            // resolved above: visual_order, then the saved moduleMapping.
            // Each door belongs to the template at templates_association[door.locker_group]
            // — scope doors per-module so cell_id lookups can't collide across templates.
            templates.map(({ tmpl, originalIndex }, i) => (
              <TemplateGridLayout
                key={tmpl.name || originalIndex}
                template={tmpl}
                doors={doors.filter((d) => d.locker_group === originalIndex)}
                selectedDoorId={selectedDoorReservation?.door?.door_id ?? null}
                onDoorClick={onDoorClick}
                moduleLabel={`${t("module")} ${String.fromCharCode(65 + i)}`}
                unlinkedDoorIds={unlinkedDoorIds}
                creatingReservationDoorId={creatingReservationDoorId}
                sessionByDoorId={sessionByDoorId}
                onRemoveReservationSession={onRemoveReservationSession}
              />
            ))
          ) : (
            // Fallback grid — matches V1's Paper styling
            <Paper
              variant="outlined"
              sx={{
                p: 0,
                pt: 3,
                bgcolor: "grey.100",
                borderRadius: 10,
                borderWidth: "5px",
                position: "relative",
              }}
            >
              {doors.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: `${5}px`,
                    p: 2,
                  }}
                >
                  {doors.map((door) => (
                    <Box
                      key={door.id}
                      sx={{
                        width: `${300 * lockerDimRatio}px`,
                        height: `${300 * lockerDimRatio}px`,
                      }}
                    >
                      <OsLockerDoorV2
                        door={door}
                        isSelected={selectedDoorReservation?.door?.door_id === door.id}
                        onClick={unlinkedDoorIds.has(door.id) ? undefined : () => onDoorClick(door)}
                        isUnlinked={unlinkedDoorIds.has(door.id)}
                        isCreatingReservation={creatingReservationDoorId === door.id}
                        session={sessionByDoorId[door.id]}
                        onRemoveReservationSession={onRemoveReservationSession}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {/* Door drawer handled by shared OsLockerDoorDrawerManager at page level */}

        {/* confirm dialog for removing all sessions — mirrors V1's dialog in
            OsLockerDashboard so both versions read identically */}
        <Dialog data-ui="UI-SL2W-049"
          open={isRemovingAllSessionsConfirming}
          onClose={() => {
            setIsRemovingAllSessionsConfirming(false)
          }}
        >
          <Box p={2}>
            <Typography variant="h6">
              {t("removeAllReservationSessions")}
            </Typography>
            <Divider sx={{ mb: 4 }} />
            <Typography variant="body1">
              {t("removeAllReservationSessionsConfirm")}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 10 }}
              justifyContent="flex-end"
            >
              <Sl2Btn
                variant="outlined"
                onClick={() => setIsRemovingAllSessionsConfirming(false)}
              >
                {t("cancel")}
              </Sl2Btn>
              <Sl2Btn
                variant="contained"
                onClick={removeAllSessions}
                disabled={isRemovingAllSessions}
              >
                {t("removeAllReservationSessions")}
              </Sl2Btn>
            </Stack>
          </Box>
        </Dialog>
      </Box>
    )
  },
)

OsLockerDashboardV2.displayName = "OsLockerDashboardV2"

export default OsLockerDashboardV2
